"use client";
import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
} from "react";
import useSWR from "swr";
import refreshUserJwt from "@/lib/auth/refreshUserJwt";
import isUserLoggedIn from "@/lib/auth/isUserLoggedIn";
import shouldLogOutUser from "@/lib/auth/shouldLogOutUser";
import getThirdPartyAuthProviders, {
  MFEContextResponse,
  TPALogistrationPage,
} from "@/lib/auth/getThirdPartyAuthProviders";
import { RecaptchaProvider } from "@/components/RecaptchaProvider";
import logoutUser from "@/lib/auth/logoutUser";

interface AuthContextType {
  isAuthReady: boolean;
  isLoggedIn: boolean;
  refreshStatus: () => void;
  isLoadingTpa: boolean;
  tpaProviders: MFEContextResponse | null;
  fetchTpaProviders: (
    page?: TPALogistrationPage,
    isModalContext?: boolean,
  ) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [isAuthReady, setIsAuthReady] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(isUserLoggedIn());
  const [shouldFetchTpaProviders, setShouldFetchTpaProviders] = useState(false);
  const [tpaParams, setTpaParams] = useState<{
    page: TPALogistrationPage;
    isModalContext: boolean;
  }>({
    page: TPALogistrationPage.RegisterOrLogin,
    isModalContext: false,
  });

  const { data: tpaProviders, isLoading: isLoadingTpa } = useSWR(
    shouldFetchTpaProviders
      ? `tpa-providers-${tpaParams.page}-${tpaParams.isModalContext}`
      : null,
    () => getThirdPartyAuthProviders(tpaParams.page, tpaParams.isModalContext),
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      dedupingInterval: 60000, // Cache for 1 minute
    },
  );

  const fetchTpaProviders = useCallback(
    (
      page: TPALogistrationPage = TPALogistrationPage.RegisterOrLogin,
      isModalContext: boolean = false,
    ) => {
      setTpaParams({ page, isModalContext });
      setShouldFetchTpaProviders(true);
    },
    [],
  );

  const refreshStatus = useCallback(() => {
    const loggedIn = isUserLoggedIn();
    setIsLoggedIn(loggedIn);
  }, []);

  useEffect(() => {
    const initializeAuth = async () => {
      await refreshUserJwt();
      setIsAuthReady(true);
      refreshStatus();
    };

    void initializeAuth();
  }, [refreshStatus]);

  useEffect(() => {
    if (!isAuthReady) {
      return;
    }

    if (shouldLogOutUser()) {
      logoutUser();
    }
  }, [isAuthReady, isLoggedIn]);

  const value = {
    isAuthReady,
    isLoggedIn,
    refreshStatus,
    isLoadingTpa,
    tpaProviders: tpaProviders || null,
    fetchTpaProviders,
  };

  return (
    <AuthContext.Provider value={value}>
      <RecaptchaProvider>{children}</RecaptchaProvider>
    </AuthContext.Provider>
  );
}
